/**
 * @description Basic page for the forgot password functionality.
 */

import { useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Book,
  Check,
  KeyRound,
  Loader2,
  X,
  ArrowLeft,
} from "lucide-react";
import api from "@/utils/axios";
import { toast } from "sonner";
import { useApp } from "@/contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ResetPasswordProps {
  navigate?: (page: string) => void;
}

export default function ResetPassword({ navigate }: ResetPasswordProps) {
  const { t } = useApp();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"input" | "loading" | "success" | "error">("input");

  const pageStyle = {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, var(--background)), color-mix(in srgb, var(--surface) 80%, var(--background)))",
  } as const;
  const iconBadgeStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 18%, var(--background))",
  } as const;
  const iconStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const successPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 8%, var(--surface))",
    borderColor:
      "color-mix(in srgb, var(--primary) 26%, var(--background))",
  } as const;
  const errorPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 8%, var(--surface))",
    borderColor:
      "color-mix(in srgb, var(--accent) 25%, var(--background))",
  } as const;
  const requirementsStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--foreground) 6%, var(--surface))",
  } as const;
  const strongPrimaryStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const mutedTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 72%, var(--background))",
  } as const;
  const dangerTextStyle = {
    color: "color-mix(in srgb, var(--accent) 78%, var(--foreground))",
  } as const;
  const requirementMetStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const requirementUnmetStyle = {
    color: "color-mix(in srgb, var(--foreground) 62%, var(--background))",
  } as const;

  const passwordRequirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword !== "";

  const goToLogin = () => {
    if (navigate) {
      navigate("login");
      return;
    }
    window.location.href = "/login";
  };

  if (!token) {
    return (
      <div
        className="flex min-h-screen items-center justify-center p-4"
        style={pageStyle}
      >
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="icon-box">
                <Book className="w-8 h-8 icon-primary" />
              </div>
              <div className="text-left">
                <h1 className="text-primary-heading">{t("header.title")}</h1>
                <p className="text-xs text-secondary">{t("header.subtitle")}</p>
              </div>
            </div>
          </div>

          <Card className="overflow-hidden border border-border/60 shadow-xl backdrop-blur-sm">
            <CardContent className="space-y-4 px-5 py-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={iconBadgeStyle}>
                <AlertCircle className="h-8 w-8" style={dangerTextStyle} />
              </div>
              <p className="text-lg font-semibold" style={dangerTextStyle}>
                {t("auth.resetInvalidLink")}
              </p>
              <p className="text-sm" style={mutedTextStyle}>
                Der Link ist ungültig oder abgelaufen.
              </p>
              <Button onClick={goToLogin} className="w-full gap-2">
                {t("auth.resetBackToLogin")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error(t("auth.resetPasswordInvalid"));
      return;
    }

    if (!passwordsMatch) {
      toast.error(t("auth.passwordsDoNotMatch"));
      return;
    }

    setStatus("loading");

    try {
      await api.post("/auth/reset-password", { token, password });
      setStatus("success");
      toast.success(t("auth.resetSuccess"));
    } catch (error: any) {
      console.error("Reset Error:", error);
      setStatus("error");
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={pageStyle}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="icon-box">
              <Book className="w-8 h-8 icon-primary" />
            </div>
            <div className="text-left">
              <h1 className="text-primary-heading">{t("header.title")}</h1>
              <p className="text-xs text-secondary">{t("header.subtitle")}</p>
            </div>
          </div>
        </div>

        <Card className="overflow-hidden border border-border/60 shadow-xl backdrop-blur-sm">
          <CardHeader className="space-y-4 pb-4 text-center">
            <div
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
              style={iconBadgeStyle}
            >
              <KeyRound className="h-8 w-8" style={iconStyle} />
            </div>
            <CardTitle className="card-title-text" style={strongPrimaryStyle}>
              {t("auth.resetTitle")}
            </CardTitle>
            <CardDescription className="card-description-text" style={mutedTextStyle}>
              {t("auth.resetSubtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {status === "success" && (
              <div
                className="space-y-4 rounded-2xl border px-5 py-6 text-center"
                style={successPanelStyle}
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--primary)_14%,var(--background))]">
                  <BadgeCheck className="h-7 w-7" style={iconStyle} />
                </div>
                <p className="text-lg font-semibold" style={strongPrimaryStyle}>
                  {t("auth.resetSuccessBody")}
                </p>
                <p className="text-sm" style={mutedTextStyle}>
                  Du kannst dich jetzt mit deinem neuen Passwort anmelden.
                </p>
                <Button onClick={goToLogin} className="w-full gap-2">
                  {t("auth.resetBackToLogin")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {status === "error" && (
              <div
                className="space-y-4 rounded-2xl border px-5 py-6 text-center"
                style={errorPanelStyle}
              >
                <AlertCircle className="mx-auto h-10 w-10" style={dangerTextStyle} />
                <p className="text-lg font-semibold" style={dangerTextStyle}>
                  {t("auth.resetErrorTitle")}
                </p>
                <p className="text-sm" style={mutedTextStyle}>
                  {t("auth.resetErrorHint")}
                </p>
                <Button variant="outline" onClick={goToLogin} className="w-full gap-2">
                  {t("auth.resetBackToLogin")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {(status === "input" || status === "loading") && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pass">{t("auth.resetPasswordLabel")}</Label>
                  <Input
                    disabled={status === "loading"}
                    id="pass"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                  />
                </div>

                {password && (
                  <div className="p-3 rounded-lg space-y-2" style={requirementsStyle}>
                    <p className="text-xs text-secondary mb-2">
                      {t("auth.passwordRequirementsTitle")}
                    </p>
                    <div className="space-y-1 text-sm">
                      {[
                        { k: "length", t: t("auth.passwordRequirementLength") },
                        { k: "uppercase", t: t("auth.passwordRequirementUppercase") },
                        { k: "lowercase", t: t("auth.passwordRequirementLowercase") },
                        { k: "number", t: t("auth.passwordRequirementNumber") },
                        { k: "special", t: t("auth.passwordRequirementSpecial") },
                      ].map((req) => (
                        <div
                          key={req.k}
                          className="flex items-center gap-2"
                          style={
                            passwordRequirements[req.k as keyof typeof passwordRequirements]
                              ? requirementMetStyle
                              : requirementUnmetStyle
                          }
                        >
                          {passwordRequirements[req.k as keyof typeof passwordRequirements] ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                          <span>{req.t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="confPass">{t("auth.resetConfirmLabel")}</Label>
                  <Input
                    disabled={status === "loading"}
                    id="confPass"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                  />

                  {confirmPassword && !passwordsMatch && (
                    <p className="text-sm flex items-center gap-1" style={dangerTextStyle}>
                      <X className="w-4 h-4" />
                      {t("auth.passwordsDoNotMatch")}
                    </p>
                  )}
                  {confirmPassword && passwordsMatch && (
                    <p className="text-sm flex items-center gap-1" style={requirementMetStyle}>
                      <Check className="w-4 h-4" />
                      {t("auth.passwordsMatch")}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={status === "loading" || !isPasswordValid || !passwordsMatch}
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("auth.resetSaving")}
                    </>
                  ) : (
                    t("auth.resetSave")
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <Button
          variant="ghost"
          onClick={() => navigate ? navigate("login") : (window.location.href = "/login")}
          className="w-full mt-4 gap-2 text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>
    </div>
  );
}
